import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { simplePostCall } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';
import '../../Styles/yellocalfcss/ViewInvoice.css';

const CustomerViewInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generateInvoice, setGenerateInvoice] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    student: '',
    academicYear: ''
  });

  // Helper: Format Date from API
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const loadInvoices = async () => {
    setIsLoading(true);
    
    // Note: Ensure these local storage keys exist in your app
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id") || 1005),
      api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
      user_id: Number(localStorage.getItem("user_id") || 14857),
    id: Number(id), // or whatever key your API expects for the bill ID

      // Depending on your API, 'id' from params might need to be passed here
      // student_id: Number(id) 
    };

    try {
      const response = await simplePostCall(
        ApiConfig.CustomerView, 
        JSON.stringify(requestBody)
      );

      if (response?.result) {
        // Map the API "model" array to the UI structure
        const formattedInvoices = response.model.map((student, index) => ({
          id: student.student_registration_id,
          invoiceId: student.student_registration_registration_number || `INV-${index + 1}`,
          student: `${student.student_registration_first_name} ${student.student_registration_last_name}`,
          academicYear: student.student_registration_academic_year || "N/A",
          demandDate: formatDate(student.student_registration_application_date), // Using app date as fallback
          dueDate: formatDate(student.student_registration_exam_date), // Using exam date as fallback
          // The API response provided didn't have specific amount fields, 
          // so I am using placeholders or specific fields if they exist in your actual full DB
          amountTotal: 0, 
          amountPaid: 0, 
          balance: 0,
        }));

        setInvoices(formattedInvoices);
      } else {
        setMessage(response?.message || "No records found");
      }
    } catch (error) {
      console.error("Error loading invoices:", error);
      setMessage("Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    return () => { document.body.style.overflow = 'auto'; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    setFilters({ student: '', academicYear: '' });
    loadInvoices(); 
  };

  const handleGenerateInvoiceToggle = () => setGenerateInvoice(!generateInvoice);

  return (
    <div className="upcoming-invoices-primary-wrapper">
      <Container fluid className="upcoming-invoices-container-wrapper">
        <div className="upcoming-invoices-header-section">
          <h1 className="upcoming-invoices-title-label">Customer Invoices</h1>
       
        </div>

        {/* Filter Section */}
        <div className="upcoming-invoices-filter-section">
          <div className="upcoming-invoices-filter-controls">
            <Form.Select 
                className="upcoming-invoices-select-student"
                value={filters.student}
                onChange={(e) => setFilters({...filters, student: e.target.value})}
            >
              <option value="">Select Student</option>
              {invoices.map(inv => (
                <option key={inv.id} value={inv.student}>{inv.student}</option>
              ))}
            </Form.Select>
            <Form.Select 
                className="upcoming-invoices-select-year"
                value={filters.academicYear}
                onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
            >
              <option value="">Select Academic Year</option>
              <option value="2020-2021">2020-2021</option>
              <option value="2023-2024">2023-2024</option>
            </Form.Select>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center my-5"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <>
            <div className="upcoming-invoices-table-header-row">
              <div className="upcoming-invoices-grid-layout">
                <div className="upcoming-invoices-header-cell">#</div>
                <div className="upcoming-invoices-header-cell">Invoice ID</div>
                <div className="upcoming-invoices-header-cell">Student</div>
                <div className="upcoming-invoices-header-cell">Academic Year</div>
                <div className="upcoming-invoices-header-cell">Demand Date</div>
                <div className="upcoming-invoices-header-cell">Due Date</div>
                <div className="upcoming-invoices-header-cell">Amount Total</div>
                <div className="upcoming-invoices-header-cell">Amount Paid</div>
                <div className="upcoming-invoices-header-cell">Balance</div>
                <div className="upcoming-invoices-header-cell upcoming-invoices-action-header">
                  <Button onClick={handleClear} className="upcoming-invoices-clear-button">Clear</Button>
                </div>
              </div>
            </div>

            <div className="upcoming-invoices-data-rows-wrapper">
              {invoices.length > 0 ? invoices.map((item, index) => (
                <div key={item.id} className="upcoming-invoices-row-card">
                  <div className="upcoming-invoices-grid-layout">
                    <div className="upcoming-invoices-data-cell">{index + 1}</div>
                    <div className="upcoming-invoices-data-cell">{item.invoiceId}</div>
                    <div className="upcoming-invoices-data-cell">{item.student}</div>
                    <div className="upcoming-invoices-data-cell">{item.academicYear}</div>
                    <div className="upcoming-invoices-data-cell">{item.demandDate}</div>
                    <div className="upcoming-invoices-data-cell">{item.dueDate}</div>
                    <div className="upcoming-invoices-data-cell">${item.amountTotal.toLocaleString()}</div>
                    <div className="upcoming-invoices-data-cell">${item.amountPaid.toLocaleString()}</div>
                    <div className="upcoming-invoices-data-cell">${item.balance.toLocaleString()}</div>
                    <div className="upcoming-invoices-data-cell upcoming-invoices-action-cell">
                      <button className="upcoming-invoices-action-icon" onClick={() => navigate(`/CustomerViewInvoice/${item.id}`)}><FaEye /></button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center p-5">No invoices found.</div>
              )}
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default CustomerViewInvoice;